import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import Footer from '../components/Footer';
import InnerPageHeader from '../components/InnerPageHeader';
import BlogCard from '../components/blog/BlogCard';
import BlogGrid from '../components/blog/BlogGrid';
import { InnerContainer, InnerSection } from '../components/InnerPageLayout';
import usePageMeta from '../hooks/usePageMeta';
import { BLOG_POSTS } from '../data/blog';
import { BlogCategory } from '../types/blogTypes';

const ALL = 'All' as const;
type Filter = BlogCategory | typeof ALL;

const BLOG_DESCRIPTION =
  'Practical guides and ideas from the Chots Team on talking things through, career prep, cooking, studying, language learning and getting more out of your Chot Buddies.';

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-bottom: 36px;
`;

const FilterButton = styled.button<{ $active: boolean }>`
  padding: 9px 18px;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? '#0f1c2d' : 'rgba(15, 28, 45, 0.15)')};
  background: ${({ $active }) => ($active ? '#0f1c2d' : 'rgba(255, 255, 255, 0.7)')};
  color: ${({ $active }) => ($active ? '#f7fbff' : '#1f2b3a')};
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${({ $active }) => ($active ? '#0f1c2d' : '#ffffff')};
  }
`;

const Blog: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<Filter>(ALL);

  usePageMeta({
    title: 'Blog | Chots AI',
    description: BLOG_DESCRIPTION,
    path: '/blog/',
  });

  const categories = useMemo<BlogCategory[]>(
    () => Array.from(new Set(BLOG_POSTS.map((post) => post.category))).sort(),
    [],
  );
  const visiblePosts = useMemo(
    () => (activeFilter === ALL ? BLOG_POSTS : BLOG_POSTS.filter((post) => post.category === activeFilter)),
    [activeFilter],
  );

  return (
    <>
      <InnerPageHeader
        data={{
          title: 'Blog',
          paragraph: 'Practical guides and ideas from the Chots Team for getting more out of your day.',
        }}
      />
      <InnerSection>
        <InnerContainer>
          <Filters role="group" aria-label="Filter posts by category">
            {[ALL, ...categories].map((category) => (
              <FilterButton
                key={category}
                type="button"
                $active={category === activeFilter}
                aria-pressed={category === activeFilter}
                onClick={() => setActiveFilter(category)}
              >
                {category}
              </FilterButton>
            ))}
          </Filters>
          <BlogGrid>
            {visiblePosts.map((post) => (
              <BlogCard key={post.slug} post={post} />
            ))}
          </BlogGrid>
        </InnerContainer>
      </InnerSection>
      <Footer />
    </>
  );
};

export default Blog;
